import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { getCurrentOrgInfo, fetchChildOrganizations, addChildOrganization } from "../api";
import AddChildOrganizationModal from "../Modals/AddChildOrganizationModal";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import { MoreHorizontal } from "lucide-react";
import { usePrivilege } from "../../../../guard/usePrivilege";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";

const TABLE_HEADERS = ["Selection", "Action", "Name", "Phone", "Email", "Office Code"];
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const SORTABLE_COLUMNS = {
  "Name": "name",
  "Phone": "phone",
  "Email": "email",
  "Office Code": "officeCode",
};

const AddChildOrganizationTab = () => {
  const { officeCode } = getCurrentOrgInfo();

  const [orgs,           setOrgs]           = useState([]);
  const [showForm,       setShowForm]       = useState(false);
  const [pageSize,       setPageSize]       = useState(PAGE_SIZE_OPTIONS[1]);
  const [page,           setPage]           = useState(1);
  const [sortBy,         setSortBy]         = useState(null);
  const [ascending,      setAscending]      = useState(true);
  const [selected,       setSelected]       = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [openDropdownRow,setOpenDropdownRow]= useState(null);
  const [dropdownPos,    setDropdownPos]    = useState({ top: 0, left: 0 });

  const dropdownRef       = useRef(null);
  const checkboxHeaderRef = useRef(null);

  const { hasPrivilege }  = usePrivilege();
  const canAddChildOrg    = hasPrivilege("officeManagement:CREATE");

  const handleSort = (header) => {
    const key = SORTABLE_COLUMNS[header];
    if (!key) return;
    if (sortBy === key) {
      setAscending(a => !a);
    } else {
      setSortBy(key);
      setAscending(true);
    }
  };

  const handleSelectAll = (checked) =>
    setSelected(checked ? orgs.map(o => o.officeCode) : []);
  const handleSingleCheckbox = (code) =>
    setSelected(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);

  const loadOrgs = async () => {
    setLoading(true);
    try {
      const res = await fetchChildOrganizations(officeCode);
      setOrgs(Array.isArray(res?.data) ? res.data : []);
    } catch {
      toast.error("Failed to fetch child organizations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrgs(); }, [officeCode]);

  // Close portal dropdown on outside click
  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenDropdownRow(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const handleActionClick = (id, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenDropdownRow((prev) => (prev === id ? null : id));
  };

  const handleAdd = async (data) => {
    try {
      await addChildOrganization(officeCode, data);
      toast.success("Child organization added.");
      setShowForm(false);
      loadOrgs();
    } catch {
      toast.error("Failed to add child organization.");
    }
  };

  const sortedOrgs = !sortBy ? orgs : [...orgs].sort((a, b) => {
    const aVal = (a[sortBy] ?? "").toString().toLowerCase();
    const bVal = (b[sortBy] ?? "").toString().toLowerCase();
    return ascending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });
  const totalPages = Math.max(1, Math.ceil(sortedOrgs.length / pageSize));
  const paged      = sortedOrgs.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">
      <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none">
        <Table
          headers={TABLE_HEADERS}
          data={paged}
          onSelectAll={handleSelectAll}
          renderCheckboxActions={() => {}}
          checkboxHeaderRef={checkboxHeaderRef}
          selectedRows={selected}
          onSort={handleSort}
          sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
          sortBy={sortBy}
          ascending={ascending}
          sortKeyByHeader={(h) => SORTABLE_COLUMNS[h]}
          stickyColumns={[0, 1, 2]}
        >
          {(org) => {
            const id     = org.officeCode;
            const isOpen = openDropdownRow === id;

            return (
              <>
                {/* Column 0 — Checkbox */}
                <td
                  className="sticky left-0 bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none"
                  style={{ width: "80px", minWidth: "80px", maxWidth: "80px", boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)" }}
                >
                  <input
                    type="checkbox"
                    className="cursor-pointer mx-auto block"
                    checked={selected.includes(org.officeCode)}
                    onChange={() => handleSingleCheckbox(org.officeCode)}
                  />
                </td>

                {/* Column 1 — Action (portal dropdown) */}
                <td
                  onClick={(e) => e.stopPropagation()}
                  className="sticky left-[80px] bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10]"
                  style={{ width: "50px", minWidth: "50px", maxWidth: "50px", boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)" }}
                >
                  <button
                    data-action-trigger
                    className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
                    onClick={(e) => handleActionClick(id, e)}
                  >
                    <MoreHorizontal size={18} className="mx-auto" />
                  </button>
                </td>

                {isOpen && ReactDOM.createPortal(
                  <div
                    ref={dropdownRef}
                    style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
                  >
                    <ActionMenu
                      rowId={id}
                      isOpen
                      onClose={() => setOpenDropdownRow(null)}
                      moduleName="ChildOrg"
                      actions={[]}
                    />
                  </div>,
                  document.body
                )}

                {/* Columns 2+ — Data */}
                {TABLE_HEADERS.slice(2).map((header, i) => {
                  const isSticky = i === 0; // "Name"
                  let content;
                  switch (header) {
                    case "Name":        content = org.name       || "-"; break;
                    case "Phone":       content = org.phone      || "-"; break;
                    case "Email":       content = org.email      || "-"; break;
                    case "Office Code": content = org.officeCode || "-"; break;
                    default:            content = "-";
                  }
                  return (
                    <td
                      key={header}
                      className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${
                        isSticky ? "sticky bg-white dark:bg-acs-dark-surface z-[9]" : ""
                      }`}
                      style={{
                        left:         isSticky ? "130px" : undefined,
                        boxShadow:    isSticky ? "2px 0 2px -1px rgba(0,0,0,0.1)" : undefined,
                        whiteSpace:   "nowrap",
                        overflow:     "hidden",
                        textOverflow: "ellipsis",
                        width:        "200px",
                        minWidth:     "200px",
                        maxWidth:     "200px",
                      }}
                    >
                      {content}
                    </td>
                  );
                })}
              </>
            );
          }}
        </Table>
      </div>

      <GlobalPagination
        label="Orgs per page"
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        itemsPerPage={pageSize}
        onItemsPerPageChange={(n) => { setPageSize(n); setPage(1); }}
        options={PAGE_SIZE_OPTIONS}
      />

      {canAddChildOrg && (
        <button
          className="fixed bottom-8 right-8 z-50 bg-blue-600 hover:bg-blue-700 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg transition"
          onClick={() => setShowForm(true)}
          title="Add Child Organization"
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      {showForm && (
        <AddChildOrganizationModal onClose={() => setShowForm(false)} onSubmit={handleAdd} />
      )}
    </div>
  );
};

export default AddChildOrganizationTab;
